import React, { useEffect, useState } from 'react';

/* =============================================================================
   CamerasBadge — etat de fraicheur des cameras, dans l'en-tete.

   Les badges En ligne / Hors ligne vivaient dans l'onglet "Aujourd'hui", retire
   du dashboard : sans cet indicateur, plus rien ne signale une camera muette.
   Volontairement compact — un point, un compteur — et rouge des qu'une boutique
   decroche, avec le detail au survol.
   ============================================================================= */

const RAFRAICHISSEMENT_MS = 60000;

const VERT = '#0f9d6b';
const ROUGE = '#ef4444';

/* 95 -> "1 min", 5400 -> "1 h 30" */
function retardLisible(sec) {
  if (sec === null || sec === undefined) return 'aucune donnee depuis 7 jours';
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 120) return `${min} min`;
  const h = Math.floor(min / 60);
  const reste = min % 60;
  return reste ? `${h} h ${reste}` : `${h} h`;
}

export default function CamerasBadge() {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let annule = false;
    const charger = () => {
      fetch('/api/cameras/status')
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then((j) => { if (!annule) { setEtat(j); setErreur(false); } })
        .catch(() => { if (!annule) setErreur(true); });
    };
    charger();
    const id = setInterval(charger, RAFRAICHISSEMENT_MS);
    return () => { annule = true; clearInterval(id); };
  }, []);

  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: '1px solid var(--border2)', borderRadius: 999,
    padding: '4px 11px', fontSize: 12, fontFamily: 'var(--font)',
    background: 'var(--surface)', whiteSpace: 'nowrap',
  };

  if (erreur) {
    return <span style={{ ...base, color: 'var(--text4)' }} title="Etat des cameras indisponible : l'API n'a pas repondu.">
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text4)' }} />
      cameras ?
    </span>;
  }
  if (!etat) return null;

  const toutesEnLigne = etat.en_ligne === etat.total;
  const couleur = toutesEnLigne ? VERT : ROUGE;

  const detail = etat.cameras
    .map((c) => `${c.boutique} — ${c.en_ligne ? 'en ligne' : 'HORS LIGNE'}, dernier point il y a ${retardLisible(c.retard_sec)}`)
    .join('\n');
  const seuilMin = Math.round((etat.seuil_sec || 1200) / 60);

  // Deux noms au plus : citer les cinq boutiques ferait deborder l'en-tete.
  // Le detail complet reste dans l'infobulle.
  let libelle = 'cameras';
  if (etat.en_ligne === 0) {
    libelle = 'cameras hors ligne';
  } else if (!toutesEnLigne) {
    const reste = etat.hors_ligne.length - 2;
    libelle = `cameras · ${etat.hors_ligne.slice(0, 2).join(', ')}${reste > 0 ? ` +${reste}` : ''}`;
  }

  return (
    <span style={{ ...base, color: toutesEnLigne ? 'var(--text2)' : ROUGE, borderColor: toutesEnLigne ? 'var(--border2)' : ROUGE }}
      title={`${detail}\n\nUne camera est consideree hors ligne au-dela de ${seuilMin} min sans nouveau point.`}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: couleur, flexShrink: 0 }} />
      <strong style={{ fontWeight: 700 }}>{etat.en_ligne}/{etat.total}</strong>
      <span>{libelle}</span>
    </span>
  );
}
